import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { BACKEND_URL } from "@/lib/api";

const RealtimeCtx = createContext(null);

function wsUrlFor(token) {
  // Convert https://... to wss://... (and http to ws) and append /api/ws?token=
  const url = new URL(BACKEND_URL);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/api/ws";
  url.search = `?token=${encodeURIComponent(token)}`;
  return url.toString();
}

/**
 * Opens a single authenticated WebSocket connection when the user is logged in,
 * with auto-reconnect (exponential backoff) and a heartbeat ping.
 * Provides:
 *  - connected: boolean
 *  - subscribe(eventType, callback): returns unsubscribe fn. Use "*" to catch all.
 *  - send(payload): low-level send
 *  - lastEvent: most recent {type, data, ts}
 */
export function RealtimeProvider({ children }) {
  const { user, setUser } = useAuth();
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const subsRef = useRef(new Map()); // type -> Set<callback>
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  const heartbeatTimer = useRef(null);
  const closedManually = useRef(false);

  const subscribe = useCallback((type, cb) => {
    if (!subsRef.current.has(type)) subsRef.current.set(type, new Set());
    subsRef.current.get(type).add(cb);
    return () => {
      const set = subsRef.current.get(type);
      if (set) {
        set.delete(cb);
        if (set.size === 0) subsRef.current.delete(type);
      }
    };
  }, []);

  const dispatch = useCallback((evt) => {
    const exact = subsRef.current.get(evt.type);
    if (exact) exact.forEach((cb) => { try { cb(evt); } catch (e) { console.debug("[realtime] subscriber error", e); } });
    const all = subsRef.current.get("*");
    if (all) all.forEach((cb) => { try { cb(evt); } catch (e) { console.debug("[realtime] wildcard subscriber error", e); } });
  }, []);

  const cleanupTimers = () => {
    if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
    if (heartbeatTimer.current) { clearInterval(heartbeatTimer.current); heartbeatTimer.current = null; }
  };

  const connect = useCallback(() => {
    const token = localStorage.getItem("samara_token");
    if (!token) return;
    closedManually.current = false;
    const url = wsUrlFor(token);
    let ws;
    try { ws = new WebSocket(url); } catch (e) { console.debug("[realtime] WebSocket ctor failed", e); return; }
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttempts.current = 0;
      setConnected(true);
      // heartbeat every 20s
      heartbeatTimer.current = setInterval(() => {
        try { ws.send("ping"); } catch (e) { console.debug("[realtime] heartbeat send failed (socket likely closing)", e); }
      }, 20000);
    };
    ws.onmessage = (msg) => {
      let evt;
      try { evt = JSON.parse(msg.data); } catch (e) { console.debug("[realtime] non-JSON payload ignored", e); return; }
      if (!evt || !evt.type) return;
      if (evt.type === "pong" || evt.type === "ping") return;

      setLastEvent(evt);

      // Auto-merge into global auth.user for wallet/BV/profile-affecting events
      if (evt.type === "wallet.update" && evt.data?.wallets) {
        setUser?.((prev) => prev ? { ...prev, wallets: { ...(prev.wallets || {}), ...evt.data.wallets } } : prev);
      }
      if (evt.type === "bv.update" && evt.data) {
        const { left_sp, right_sp, total_sp, user_code } = evt.data;
        setUser?.((prev) => {
          if (!prev) return prev;
          // Only merge if event is about this user (own legs)
          if (user_code && prev.user_code && user_code !== prev.user_code) return prev;
          return { ...prev, left_sp, right_sp, total_sp };
        });
      }
      if (evt.type === "hello" && evt.data) {
        const { wallets, left_sp, right_sp, total_sp } = evt.data;
        setUser?.((prev) => prev ? { ...prev, wallets: { ...(prev.wallets || {}), ...(wallets || {}) }, left_sp: left_sp ?? prev.left_sp, right_sp: right_sp ?? prev.right_sp, total_sp: total_sp ?? prev.total_sp } : prev);
      }

      dispatch(evt);
    };
    ws.onclose = () => {
      cleanupTimers();
      setConnected(false);
      wsRef.current = null;
      if (closedManually.current) return;
      // exponential backoff: 1s, 2s, 4s ... cap 30s
      const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttempts.current));
      reconnectAttempts.current += 1;
      reconnectTimer.current = setTimeout(connect, delay);
    };
    ws.onerror = () => {
      // Closing an already-broken socket can itself throw — swallow and log.
      try { ws.close(); } catch (e) { console.debug("ws.close after error threw:", e?.message); }
    };
  }, [dispatch, setUser]);

  const disconnect = useCallback(() => {
    closedManually.current = true;
    cleanupTimers();
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (e) { console.debug("ws.close during disconnect threw:", e?.message); }
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  useEffect(() => {
    if (user) connect();
    else disconnect();
    return () => disconnect();
  }, [user?.id, connect, disconnect]);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try { ws.send(typeof payload === "string" ? payload : JSON.stringify(payload)); return true; } catch (e) { return false; }
  }, []);

  return (
    <RealtimeCtx.Provider value={{ connected, subscribe, send, lastEvent }}>
      {children}
    </RealtimeCtx.Provider>
  );
}

export const useRealtime = () => useContext(RealtimeCtx) || { connected: false, subscribe: () => () => {}, send: () => false, lastEvent: null };
